import { useState, useEffect, useCallback } from 'react'
import axios from 'axios'
import { getApiBaseUrl } from '@/lib/apiBase'

const API_URL = getApiBaseUrl()
const STORAGE_KEY_PREFIX = 'thachari_popup_'

// Keyed per user id — the dismiss-count/last-shown/permanently-suppressed state
// must not leak across accounts sharing a browser. A single global key here
// previously meant dismissing the popup (or letting the 24h hours_between_shows
// cooldown start) as one account silenced it for every other account signed
// into that browser afterward, including brand-new signups (this is what was
// actually behind "the Share & Earn popup is gone for new users" — the popup
// itself was never broken, its cooldown state just wasn't scoped to the user).
function getStorageData(userId) {
  if (!userId) return {}
  try {
    return JSON.parse(localStorage.getItem(STORAGE_KEY_PREFIX + userId) || '{}')
  } catch {
    return {}
  }
}

function setStorageData(userId, data) {
  if (!userId) return
  try {
    localStorage.setItem(STORAGE_KEY_PREFIX + userId, JSON.stringify({
      ...getStorageData(userId),
      ...data
    }))
  } catch {}
}

// Self-contained like AdmissionCounter.jsx — fetches its own settings/share/admission
// data rather than requiring a parent to thread them through, since this app has no
// shared settings context.
export function usePromoPopup(user) {
  const [showPopup, setShowPopup] = useState(false)
  const [popupConfig, setPopupConfig] = useState(null)
  const [admissionCount, setAdmissionCount] = useState(null)
  const [dataLoaded, setDataLoaded] = useState(false)

  const shouldShow = useCallback(() => {
    if (!user) return false
    if (user.is_premium) return false
    if (user.subscription_type === 'lifetime' || user.subscription_plan === 'lifetime') return false
    if (!popupConfig?.enabled) return false

    const storage = getStorageData(user.id)

    if (storage.permanentlySuppressed || user.popup_permanently_suppressed) {
      return false
    }

    const maxDismissals = popupConfig?.max_dismissals ?? 3
    const dismissCount = storage.dismissCount || 0
    if (dismissCount >= maxDismissals) return false

    const hoursBetween = popupConfig?.hours_between_shows ?? 24
    const lastShown = storage.lastShown ? new Date(storage.lastShown) : null
    if (lastShown) {
      const hoursSince = (Date.now() - lastShown.getTime()) / (1000 * 60 * 60)
      if (hoursSince < hoursBetween) return false
    }

    return true
  }, [user, popupConfig])

  // Fetch platform settings (popup_config) + supporting data
  useEffect(() => {
    if (!user || user.is_premium) return undefined

    let mounted = true
    const fetchData = async () => {
      try {
        const [settingsRes, admissionRes] = await Promise.allSettled([
          axios.get(`${API_URL}/settings`),
          axios.get(`${API_URL}/admissions/count`),
        ])
        if (!mounted) return
        if (settingsRes.status === 'fulfilled') setPopupConfig(settingsRes.value.data?.popup_config || null)
        if (admissionRes.status === 'fulfilled') setAdmissionCount(admissionRes.value.data)
      } catch {
        // never block the app over popup data
      } finally {
        if (mounted) setDataLoaded(true)
      }
    }

    fetchData()
    return () => { mounted = false }
  }, [user])

  // Trigger on login
  useEffect(() => {
    if (!dataLoaded) return undefined
    if (!popupConfig?.show_on_login) return undefined
    if (!shouldShow()) return undefined

    const delay = (popupConfig?.delay_seconds ?? 2) * 1000
    const timer = setTimeout(() => setShowPopup(true), delay)

    return () => clearTimeout(timer)
  }, [dataLoaded, shouldShow, popupConfig])

  // Trigger on lesson complete (call from LessonPlayer)
  const triggerOnLessonComplete = useCallback(() => {
    if (!popupConfig?.show_on_lesson_complete) return
    if (!shouldShow()) return

    const delay = (popupConfig?.delay_seconds ?? 2) * 1000
    setTimeout(() => setShowPopup(true), delay)
  }, [shouldShow, popupConfig])

  const handleClose = useCallback(() => {
    setShowPopup(false)
    setStorageData(user?.id, {
      lastShown: new Date().toISOString(),
      dismissCount: (getStorageData(user?.id).dismissCount || 0) + 1
    })
  }, [user])

  const handlePermanentSuppress = useCallback(() => {
    setShowPopup(false)
    setStorageData(user?.id, { permanentlySuppressed: true })
  }, [user])

  return {
    showPopup,
    popupConfig,
    admissionCount,
    handleClose,
    handlePermanentSuppress,
    triggerOnLessonComplete
  }
}
